import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  Tags, 
  Users, 
  FileText, 
  BarChart3, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Store,
  UserPlus,
  History,
  CreditCard,
  Banknote,
  Settings,
  Receipt,
  RotateCcw,
  FileSliders,
  ImageIcon,
  TrendingUp,
  QrCode,
  Search,
  Wrench,
  Truck,
  ShieldAlert,
  Bell,
  AlertTriangle,
  Clock,
  DollarSign,
  FolderOpen,
  Folder,
  Warehouse,
  Landmark,
  ShieldCheck,
  Building,
  ArrowRightLeft,
  ClipboardList
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const LAYOUT_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standalone items (no folder)
const standaloneItems = [
  { id: 'dashboard', to: '/admin', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard', end: true },
  { id: 'pos', to: '/admin/pos', icon: Banknote, label: 'Punto de Venta', module: 'pos' },
  { id: 'support', to: '/admin/support', icon: Wrench, label: 'Soporte Técnico', module: 'support' },
  { id: 'returns', to: '/admin/returns', icon: RotateCcw, label: 'Devoluciones', module: 'returns' },
  { id: 'products', to: '/admin/products', icon: Package, label: 'Productos', module: 'products' },
  { id: 'marketplace', to: '/admin/marketplace', icon: Store, label: 'Laptops (Marketplace)', module: 'products' },
  { id: 'equipment-lookup', to: '/admin/equipment-lookup', icon: Search, label: 'Consultar Equipo', module: 'equipment_lookup' },
  { id: 'product-history', to: '/admin/product-history', icon: ClipboardList, label: 'Historial Producto', module: 'product_history' },
];

// Grouped items in folders
const navGroups = [
  {
    id: 'inventory-group',
    label: 'Inventario',
    icon: Warehouse,
    items: [
      { id: 'inventory-scan', to: '/admin/inventory-scan', icon: QrCode, label: 'Inventario QR', module: 'inventory_scan' },
      { id: 'stock-transfers', to: '/admin/stock-transfers', icon: ArrowRightLeft, label: 'Traslados', module: 'branches' },
      { id: 'suppliers', to: '/admin/suppliers', icon: Truck, label: 'Proveedores', module: 'suppliers' },
      { id: 'purchases', to: '/admin/purchases', icon: Package, label: 'Compras', module: 'purchases' },
      { id: 'inventory-aging', to: '/admin/inventory-aging', icon: Clock, label: 'Rotación Inventario', module: 'inventory_aging' },
      { id: 'lost-damaged', to: '/admin/lost-damaged', icon: ShieldAlert, label: 'Perdidos/Dañados', module: 'lost_damaged' },
    ]
  },
  {
    id: 'finance-group',
    label: 'Finanzas',
    icon: Landmark,
    items: [
      { id: 'finance', to: '/admin/finance', icon: DollarSign, label: 'Finanzas', module: 'finance' },
      { id: 'credits', to: '/admin/credits', icon: CreditCard, label: 'Créditos', module: 'credits' },
      { id: 'corporate-credits', to: '/admin/corporate-credits', icon: Building, label: 'Crédito Empresarial', module: 'corporate_credits' },
      { id: 'reports', to: '/admin/reports', icon: BarChart3, label: 'Reportes', module: 'reports' },
      { id: 'sales-history', to: '/admin/sales-history', icon: Receipt, label: 'Historial Ventas', module: 'sales_history' },
      { id: 'quotations', to: '/admin/quotations', icon: FileText, label: 'Cotizaciones', module: 'quotations' },
    ]
  },
  {
    id: 'admin-group',
    label: 'Administrativo',
    icon: ShieldCheck,
    items: [
      { id: 'audit-log', to: '/admin/audit-log', icon: History, label: 'Actividad', module: 'audit_log' },
      { id: 'settings', to: '/admin/settings', icon: Settings, label: 'Configuraciones', module: 'settings' },
      { id: 'shop-config', to: '/admin/shop-config', icon: ImageIcon, label: 'Config. Tienda', module: 'shop_config' },
      { id: 'invoice-settings', to: '/admin/invoice-settings', icon: FileSliders, label: 'Plantillas PDF', module: 'invoice_settings' },
      { id: 'users', to: '/admin/users', icon: UserPlus, label: 'Usuarios', module: 'users' },
      { id: 'branches', to: '/admin/branches', icon: Building, label: 'Sucursales', module: 'branches' },
      { id: 'b2b-customers', to: '/admin/b2b-customers', icon: Building, label: 'Empresas (B2B)', module: 'b2b_customers' },
      { id: 'analytics', to: '/admin/analytics', icon: TrendingUp, label: 'Estadísticas', module: 'analytics' },
      { id: 'customers', to: '/admin/customers', icon: Users, label: 'Clientes', module: 'customers' },
      { id: 'categories', to: '/admin/categories', icon: Tags, label: 'Categorías', module: 'categories' },
      { id: 'product-tags', to: '/admin/product-tags', icon: Tags, label: 'Etiquetas de Uso', module: 'product_tags' },
    ]
  }
];

// Flat list for backward compatibility (reorder, permissions)
const defaultNavItems = [
  ...standaloneItems,
  ...navGroups.flatMap(g => g.items),
];

// Branch indicator + switcher shown in the header
function BranchIndicator() {
  const { branch, setBranch } = useAuth();
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);

  const loadBranches = () => {
    axios.get(`${LAYOUT_API}/branches/public`)
      .then(res => setBranches(res.data || []))
      .catch(() => setBranches([]));
  };

  useEffect(() => { loadBranches(); }, []);

  if (!branch) return null;
  const Icon = branch.is_warehouse ? Warehouse : Store;

  const pick = (b) => {
    setBranch(b);
    setOpen(false);
  };

  const toggleOpen = () => {
    if (!open) loadBranches();
    setOpen(!open);
  };

  return (
    <div className="relative">
      <button
        onClick={toggleOpen}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700/60 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
        data-testid="branch-indicator"
      >
        <Icon className={`w-4 h-4 ${branch.is_warehouse ? 'text-amber-500' : 'text-blue-500'}`} />
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200 max-w-[140px] truncate">{branch.name}</span>
        {branches.length > 1 && <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && branches.length > 1 && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-50 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden" data-testid="branch-switcher-dropdown">
            <div className="px-3 py-2 text-xs font-semibold text-slate-400 border-b border-slate-100 dark:border-slate-700">Cambiar sucursal</div>
            {branches.map(b => {
              const BIcon = b.is_warehouse ? Warehouse : Store;
              return (
                <button
                  key={b.id}
                  onClick={() => pick(b)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/60 ${b.id === branch.id ? 'bg-primary/5 text-primary font-medium' : 'text-slate-700 dark:text-slate-200'}`}
                  data-testid={`branch-switch-${b.id}`}
                >
                  <BIcon className={`w-4 h-4 ${b.is_warehouse ? 'text-amber-500' : 'text-blue-500'}`} />
                  <span className="truncate">{b.name}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


// Collapsible Folder Component
function NavFolder({ group, hasModuleAccess, onClose }) {
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem(`supertech_folder_${group.id}`);
    return saved === null ? false : saved === 'true';
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(`supertech_folder_${group.id}`, String(next));
  };

  const visibleItems = group.items.filter(item => hasModuleAccess(item.module));
  if (visibleItems.length === 0) return null;

  const Icon = group.icon;

  return (
    <div className="mb-1">
      <button
        onClick={toggle}
        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
        data-testid={`folder-${group.id}`}
      >
        <Icon className="w-5 h-5 shrink-0" />
        <span className="text-sm font-medium flex-1 text-left">{group.label}</span>
        {open ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
      </button>
      {open && (
        <div className="ml-3 pl-3 border-l border-slate-700/50 mt-0.5 space-y-0.5">
          {visibleItems.map(item => (
            <NavLink
              key={item.id}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) => `
                flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm
                transition-colors duration-200
                ${isActive
                  ? 'bg-primary/20 text-primary font-medium'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
              `}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout, hasModuleAccess } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCosts, setPendingCosts] = useState([]);
  const [showPendingDropdown, setShowPendingDropdown] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch pending cost serials (admin only)
  useEffect(() => {
    if (user?.role === 'admin') {
      const fetchPending = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${LAYOUT_API}/products/pending-costs/all`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPendingCosts(res.data);
        } catch (e) { /* ignore */ }
      };
      fetchPending();
      const interval = setInterval(fetchPending, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.role]);

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 bottom-0 w-64 z-50
        bg-slate-900 text-white
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <img 
              src="https://customer-assets.emergentagent.com/job_d0ebb01a-3628-4f72-9656-9b72303a57b5/artifacts/fdp6yzo5_logo.png" 
              alt="Supertech" 
              className="h-8"
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin">
            {/* Standalone items */}
            {standaloneItems
              .filter(item => hasModuleAccess(item.module))
              .map(item => (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.end}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-4 py-3 rounded-lg mb-1
                    transition-colors duration-200
                    ${isActive
                      ? 'bg-primary/20 text-primary font-medium'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                  `}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))
            }

            {/* Separator */}
            <div className="my-2 border-t border-slate-800" />

            {/* Folder groups */}
            {navGroups.map(group => (
              <NavFolder
                key={group.id}
                group={group}
                hasModuleAccess={hasModuleAccess}
                onClose={() => setSidebarOpen(false)}
              />
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 text-slate-400 hover:text-white hover:bg-slate-800"
                onClick={() => window.open('/', '_blank')}
                data-testid="view-store-button"
              >
                <Store className="w-4 h-4 mr-2" />
                Tienda
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <div className="flex-1 lg:flex-none">
              {/* Breadcrumb or page title can go here */}
            </div>

            <div className="flex items-center gap-4">
              {/* Branch indicator */}
              <BranchIndicator />

              {/* Pending costs notification bell */}
              {user?.role === 'admin' && pendingCosts.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowPendingDropdown(!showPendingDropdown)}
                    className="relative p-2 rounded-lg hover:bg-red-50 transition-colors"
                    data-testid="pending-costs-bell"
                  >
                    <Bell className="w-5 h-5 text-red-500 animate-pulse" />
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {pendingCosts.length}
                    </span>
                  </button>

                  {showPendingDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowPendingDropdown(false)} />
                      <div className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-16 sm:top-12 z-50 sm:w-96 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden" data-testid="pending-costs-dropdown">
                        <div className="bg-red-500 text-white px-4 py-3 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 shrink-0" />
                          <span className="font-semibold text-sm truncate">URGENTE - Costos Pendientes</span>
                          <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-xs font-bold shrink-0">{pendingCosts.length}</span>
                        </div>
                        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                          {pendingCosts.map((item, idx) => (
                            <button
                              key={idx}
                              className="w-full px-3 sm:px-4 py-3 flex items-start gap-2 sm:gap-3 hover:bg-slate-50 transition-colors text-left"
                              onClick={() => {
                                setShowPendingDropdown(false);
                                navigate('/admin/products');
                              }}
                              data-testid={`pending-item-${idx}`}
                            >
                              <div className="p-1.5 bg-amber-100 rounded-md mt-0.5 shrink-0">
                                <Clock className="w-3.5 h-3.5 text-amber-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-800 truncate">{item.product_name}</p>
                                <p className="text-xs font-mono text-slate-500 truncate">{item.serial}</p>
                                <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400">
                                  {item.added_by && <span className="truncate">por {item.added_by}</span>}
                                  {item.added_at && (
                                    <span className="shrink-0">{new Date(item.added_at).toLocaleDateString('es-PA', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'})}</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0 mt-1">
                                <DollarSign className="w-3.5 h-3.5 text-red-400" />
                                <span className="text-xs font-semibold text-red-500 hidden sm:inline">Sin costo</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        <div className="border-t border-slate-200 px-4 py-2.5 bg-slate-50">
                          <button
                            className="w-full text-center text-sm text-blue-600 font-medium hover:text-blue-700"
                            onClick={() => { setShowPendingDropdown(false); navigate('/admin/products'); }}
                          >
                            Ir a Productos para asignar costos
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <span className="text-sm text-slate-600 dark:text-slate-400">
                Hola, <span className="font-medium text-slate-900 dark:text-white">{user?.name}</span>
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
